import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Search, Eye, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import CloseIcon from "@mui/icons-material/Close";
import { useGetApi } from "../hooks/useGetApi";
import { getPatientApi } from "../services/patient.service";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import {
  DEFAULT_LIMIT,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from "../utils/constants";

export default function Consultation() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  const [search, setSearch] = useState("");
  const [searchBy, setSearchBy] = useState("firstName");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_LIMIT,
  });
  const [sortModel, setSortModel] = useState([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchContainerRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get patients from API
  // Note: Fetching all patients without server-side pagination to enable proper client-side filtering and pagination
  // Since we filter for Active OR IPD status, we need all data before filtering
  const {
    dataList: patients,
    isLoading,
    isError,
    refetch,
  } = useGetApi({
    apiFunction: getPatientApi,
    params: { search, searchBy, page: 1, limit: 10000 },
    dependencies: [search, searchBy],
    debounceDelay: 500,
    dataKey: "patients",
    countKey: "total",
  });

  const handleClose = () => {
    navigate(-1);
  };

  const handleStartConsultation = (patientId) => {
    navigate(`/consultation/${patientId}`);
  };

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
  };

  // Function to calculate age from date of birth
  const calculateAge = (dob) => {
    if (!dob) return 'N/A';
    const birthDate = new Date(dob);
    const today = new Date();
    const age = Math.floor((today - birthDate) / (1000 * 60 * 60 * 24 * 365.25));
    return age >= 0 ? age : 'N/A';
  };

  const filteredPatients = patients?.filter(patient => {
    // Show patients who are admitted (IPD) or have active visits
    return patient.status === 'Active' || patient.status === 'IPD';
  }) || [];

  // Prepare rows for DataGrid
  const rows = filteredPatients?.map((patient) => {
    return {
      id: patient._id,
      _id: patient._id,
      UHID: patient.UHID || 'N/A',
      name: `${patient.firstName || ""} ${patient.lastName || ""}`.trim(),
      age: calculateAge(patient.dob),
      gender: patient.gender || 'N/A',
      contact: patient.contactNumber || patient.phone || 'N/A',
      visitType: patient.visitType || 'OPD',
      status: patient.status || 'Active',
    };
  }) || [];

  // Responsive columns configuration
  const getColumns = useCallback(() => {
    const baseColumns = [
      {
        field: 'UHID',
        headerName: 'UHID',
        flex: isMobile ? 0 : 0.8,
        width: isMobile ? 100 : undefined,
        minWidth: 90,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
      },
      {
        field: 'name',
        headerName: 'Patient Name',
        flex: isMobile ? 0 : 1.2,
        width: isMobile ? 120 : undefined,
        minWidth: 120,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
      },
      {
        field: 'age',
        headerName: 'Age',
        flex: isMobile ? 0 : 0.5,
        width: isMobile ? 60 : undefined,
        minWidth: 50,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
      },
      {
        field: 'gender',
        headerName: 'Gender',
        flex: isTablet ? 0 : 0.7,
        width: isTablet ? 80 : undefined,
        minWidth: 70,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
      },
      {
        field: 'contact',
        headerName: 'Contact',
        flex: isMobile ? 0 : 1,
        width: isMobile ? 110 : undefined,
        minWidth: 110,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
      },
      {
        field: 'visitType',
        headerName: 'Visit Type',
        flex: isTablet ? 0 : 0.8,
        width: isTablet ? 100 : undefined,
        minWidth: 90,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
        renderCell: (params) => (
          <Chip
            label={params.value || 'OPD'}
            size="small"
            color={params.value === 'IPD' ? 'error' : 'primary'}
            variant="outlined"
          />
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: isTablet ? 0 : 0.8,
        width: isTablet ? 80 : undefined,
        minWidth: 70,
        headerAlign: 'center',
        align: 'center',
        sortable: true,
        renderCell: (params) => (
          <Chip
            label={params.value || 'Active'}
            size="small"
            color={
              params.value === 'IPD' ? 'error' :
              params.value === 'Active' ? 'success' : 'default'
            }
            variant="outlined"
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 0,
        width: 70,
        minWidth: 60,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        renderCell: (params) => (
          <Tooltip title="Start Consultation">
            <IconButton
              color="primary"
              size={isMobile ? "small" : "medium"}
              onClick={(e) => {
                e.stopPropagation();
                handleStartConsultation(params.row._id);
              }}
              sx={{
                backgroundColor: "#e3f2fd",
                "&:hover": { backgroundColor: "#bbdefb" }
              }}
            >
              <Eye size={isMobile ? 16 : 18} />
            </IconButton>
          </Tooltip>
        ),
      },
    ];

    // Responsive column hiding
    if (isMobile) {
      return baseColumns.filter(col => 
        ['UHID', 'name', 'actions'].includes(col.field)
      );
    }
    if (isTablet) {
      return baseColumns.filter(col => 
        ['UHID', 'name', 'age', 'contact', 'actions'].includes(col.field)
      );
    }
    return baseColumns;
  }, [isMobile, isTablet]);

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ 
          width: "100%", 
          pr: { xs: "10px", sm: "20px", md: "40px", lg: "60px", xl: "90px" },
          pl: { xs: "10px", sm: "20px", md: "20px" },
          maxWidth: "100vw",
          overflow: "hidden"
        }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              my: "20px",
              color: "#5D6A82",
            }}
          >
            <Typography sx={{ 
              color: "#5D6A82", 
              fontFamily: "Open Sans",
              fontWeight: 600,
              fontSize: "22px",
              lineHeight: "29px",
              letterSpacing: "0px"
            }}>Consultation</Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Search and Actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: "20px",
              marginBottom: "40px",
            }}
          >
            <Box
              ref={searchContainerRef}
              sx={{
                display: "flex",
                backgroundColor: "white",
                border: isSearchFocused ? "2px solid #0179B0" : "1px solid #e0e0e0",
                borderRadius: "8px",
                alignItems: "center",
                position: "relative",
                boxShadow: isSearchFocused 
                  ? "0 4px 12px rgba(1, 121, 176, 0.15)" 
                  : "0 2px 4px rgba(0, 0, 0, 0.05)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                  borderColor: "#c0c0c0"
                }
              }}
            >
              {/* Dropdown - only show when search is focused */}
              {isSearchFocused && (
                <select
                  onChange={(e) => setSearchBy(e.target.value)}
                  value={searchBy}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    padding: "12px 16px",
                    fontSize: "14px",
                    width: `${Math.max(120, (searchBy === "firstName" ? "Name" : 
                      searchBy === "UHID" ? "UHID" : 
                      searchBy === "phone" ? "Contact" : "Name").length * 9 + 30)}px`,
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    borderRight: "1px solid #e0e0e0",
                    color: "#333",
                    fontWeight: "500",
                    touchAction: "manipulation"
                  }}
                >
                  <option value="firstName">Name</option>
                  <option value="UHID">UHID</option>
                  <option value="phone">Contact</option>
                </select>
              )}
              
              {/* Search Input */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "12px 16px",
                  width: "300px",
                  transition: "all 0.3s ease",
                  gap: "10px"
                }}
              >
                <Search 
                  size={20} 
                  style={{ 
                    color: isSearchFocused ? "#0179B0" : "#666",
                    transition: "color 0.3s ease",
                    flexShrink: 0
                  }} 
                />
                <input
                  type="text"
                  placeholder={`Search by ${searchBy === "firstName" ? "Name" : 
                    searchBy === "UHID" ? "UHID" : 
                    searchBy === "phone" ? "Contact" : "Name"}`}
                  value={search || ""}
                  onChange={(e) => setSearch(e.target.value || "")}
                  onFocus={() => setIsSearchFocused(true)}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    width: "100%",
                    fontSize: "14px",
                    color: "#333",
                    padding: "0",
                    minHeight: "24px",
                    touchAction: "manipulation",
                    WebkitTapHighlightColor: "transparent"
                  }}
                />
                {search && (
                  <X 
                    size={18} 
                    style={{ 
                      cursor: "pointer", 
                      color: "#999",
                      flexShrink: 0,
                      padding: "2px",
                      transition: "all 0.2s ease",
                      touchAction: "manipulation"
                    }}
                    onClick={() => setSearch("")}
                    onMouseEnter={(e) => e.target.style.color = "#0179B0"}
                    onMouseLeave={(e) => e.target.style.color = "#999"}
                  />
                )}
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                variant="outlined"
                sx={{ textTransform: "none" }}
                onClick={() => refetch()}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          {/* Instructions */}
          <Box sx={{ marginBottom: "40px", p: 2, bgcolor: "#f0f8ff", borderRadius: 1, border: "1px solid #e3f2fd" }}>
            <Typography variant="body2" color="primary">
              💡 Select a patient from the list below to start consultation. Only active and admitted patients are shown.
            </Typography>
          </Box>

          {/* DataGrid Table */}
          {isLoading ? (
            <Loader />
          ) : isError ? (
            <ErrorMessage />
          ) : (
            <Box sx={{ 
              height: { 
                xs: 'calc(100vh - 300px)', 
                sm: 'calc(100vh - 320px)', 
                md: 'calc(100vh - 350px)' 
              },
              width: '100%',
              minHeight: '300px',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <DataGrid
                rows={rows}
                columns={getColumns()}
                paginationModel={paginationModel}
                onPaginationModelChange={handlePaginationModelChange}
                pageSizeOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                paginationMode="client"
                sortingMode="client"
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
                disableRowSelectionOnClick
                loading={isLoading}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-root': {
                    border: 'none',
                    width: '100%',
                  },
                  '& .MuiDataGrid-main': {
                    width: '100%',
                  },
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid #f0f0f0',
                    padding: isMobile ? '4px' : '8px',
                    fontSize: isMobile ? '12px' : '14px',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f5f5f5',
                    borderBottom: '2px solid #e0e0e0',
                  },
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: '#f5f5f5',
                    fontWeight: 'bold',
                    fontSize: isMobile ? '12px' : '14px',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 'bold',
                    fontSize: isMobile ? '12px' : '14px',
                  },
                  '& .MuiDataGrid-row': {
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      cursor: 'pointer',
                    },
                    '&:nth-of-type(odd)': {
                      backgroundColor: '#fafafa',
                    },
                    '&:nth-of-type(even)': {
                      backgroundColor: 'white',
                    },
                  },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: '2px solid #e0e0e0',
                    backgroundColor: '#f9f9f9',
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    minHeight: '200px',
                  },
                  [theme.breakpoints.down('sm')]: {
                    '& .MuiDataGrid-columnHeader': {
                      fontSize: '11px',
                      padding: '4px',
                    },
                    '& .MuiDataGrid-cell': {
                      fontSize: '11px',
                      padding: '2px 4px',
                    },
                  },
                }}
                components={{
                  NoRowsOverlay: () => (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography variant="body2" color="text.secondary">
                        No patients available for consultation
                      </Typography>
                    </Box>
                  ),
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Container>
  );
} 